import { useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";

import {
  EuiAvatar,
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiPopoverTitle,
  EuiSelectable,
  EuiText,
  EuiSpacer,
  EuiIcon,
} from "@elastic/eui";
import Clients from "services/ClientService/Clients";
import Emitter from "services/UtilsService/Emitter";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";
import { authSelector } from "reduxStore/auth/auth.selectors";
import { isNullOrUndefined, sortListByProperty } from "utils";
import Popover from "components/Popover/Popover";
import Modal from "components/Modal/Modal";
import Button from "components/Buttons/Button";
import moment from "moment-timezone";
import { REDIRECT_TO_SETUP_WIZARD } from "settings/menu/configuration";
import { REDIRECT_TO_MAIN_PAGE } from "settings/menu/dashboardsPages";

import "styles/global/colors.css";
import "styles/global/page.css";

export const ClientSelect = ({
  ClassName = "clients-select",
  ShowAvatar = false,
  LinkColor = "text",
  IsDisabled = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [isClientsSelectOpen, setIsClientsSelectOpen] = useState<boolean>(false);
  const [isModalOpen, setModalOpen] = useState<boolean>(false);
  const { allowedClients } = useSelector(authSelector, shallowEqual);
  const { clientInfo } = useSelector(globalFilterSelector, shallowEqual);
  const [clientName, setClientName] = useState<string>("");
  const [expirationDate, setExpirationDate] = useState<string>("");
  const noSubscription = expirationDate?.startsWith("0000");

  const toggleClientsSelect = () => {
    setIsClientsSelectOpen(!isClientsSelectOpen);
    Emitter.emit("pageUnload", () => {});
  };

  const closeClientsSelect = () => {
    setIsClientsSelectOpen(false);
  };

  const closeExpireModal = () => {
    setClientName("");
    setExpirationDate("");
    setModalOpen(false);
    setIsClientsSelectOpen(true);
  };

  const onClientChange = async (clientList: any, e: any, changedOption: any) => {
    e.stopPropagation();
    e.preventDefault();
    const selectedClient = changedOption; //clientList.find((c: any) => c.checked === "on");
    closeClientsSelect();
    if (!isNullOrUndefined(selectedClient?.id)) {
      const subscriptionResult = await Clients.subscriptionCheck(selectedClient.id);
      if (subscriptionResult?.isExpired) {
        setClientName(selectedClient?.label);
        setExpirationDate(
          moment.utc(subscriptionResult.expirationUtc).local().format("YYYY MMM D hh:mm A")
        );
        setModalOpen(true);
      }

      await Clients.registerCacheHub(selectedClient.id);
    }

    const runOnboarding = allowedClients?.find(
      (i: any) => i?.clientID === selectedClient?.id
    )?.runOnboarding;

    if (!runOnboarding && location?.pathname === REDIRECT_TO_SETUP_WIZARD) {
      navigate(REDIRECT_TO_MAIN_PAGE);
    }

    if (runOnboarding) {
      navigate(REDIRECT_TO_SETUP_WIZARD);
    }

    await Clients.loadGlobalClient(selectedClient ? selectedClient.id : null);
  };

  const isLessThen = allowedClients?.length <= 10;
  const height = isLessThen ? allowedClients.length * 32 : 416;
  const displayName =
    clientInfo.displayName?.length > 20
      ? clientInfo.displayName
          ?.split("")
          .filter((_: any, idx: number) => idx <= 18)
          .join("") + "..."
      : clientInfo.displayName;

  return (
    <div>
      {!IsDisabled && (
        <EuiFlexGroup
          className="client_selector"
          style={{ cursor: "pointer", display: "flex", alignItems: "center" }}
          responsive={false}>
          <Popover
            anchorPosition="downRight"
            offset={12}
            button={
              <EuiButtonEmpty
                // @ts-ignore
                color={LinkColor}
                onClick={toggleClientsSelect}
                iconType={"arrowDown"}
                iconSide="right"
                size="xs"
                style={{
                  fontSize: "var(--fs14)",
                  fontWeight: 400,
                  color: "#fff",
                  paddingInline: "0px",
                }}
                title={clientInfo.displayName}>
                <EuiFlexGroup
                  style={{ display: "inline-flex", alignItems: "center" }}
                  gutterSize="s">
                  {ShowAvatar && (
                    <EuiFlexItem grow={false}>
                      <EuiAvatar
                        style={{ borderRadius: "6px" }}
                        color="#8b7cf0"
                        // id="profile-avatar"
                        name={clientInfo.displayName}
                        size="s"
                      />
                    </EuiFlexItem>
                  )}
                  <EuiFlexItem>{displayName}</EuiFlexItem>
                </EuiFlexGroup>
              </EuiButtonEmpty>
            }
            onClose={closeClientsSelect}
            isOpen={!IsDisabled && isClientsSelectOpen}
            children={
              <EuiSelectable
                style={{ borderRadius: "var(--box-radius) !important" }}
                height={height}
                onChange={onClientChange}
                options={sortListByProperty(
                  allowedClients.map((client: any) => ({
                    label: client.displayName,
                    id: client.clientID,
                    showIcons: false,
                    onFocusBadge: false,
                    "data-test-subj": "client-item-" + client.displayName,
                    className: "client-item",
                    checked: client.clientID === clientInfo.clientID ? "on" : "",
                  })),
                  "label",
                  "asc"
                )}
                searchProps={{
                  inputRef: (node: HTMLInputElement | null) => {
                    if (node) node?.focus({ preventScroll: true });
                  },
                  placeholder: "Search...",
                  compressed: true,
                  className: "clients-search",
                  css: {
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                    borderBottom: ".6px solid var(--border-color)",
                    boxShadow: "none",
                    backgroundColor: "var(--card-background)",
                  },
                }}
                searchable
                singleSelection>
                {(list, search) => (
                  <div style={{ width: 230 }}>
                    <EuiPopoverTitle
                      style={{
                        backgroundColor: "var(--card-background)",
                        borderRadius: "var(--box-radius)",
                      }}>
                      {search}
                    </EuiPopoverTitle>
                    {list}
                  </div>
                )}
              </EuiSelectable>
            }
          />
        </EuiFlexGroup>
      )}

      {isModalOpen && (
        <Modal
          title={noSubscription ? "No Subscription" : "Subscription is expired"}
          onClose={closeExpireModal}
          maxWidth={650}
          children={
            <EuiFlexGroup alignItems="center" responsive={false} style={{ padding: "16px 0px " }}>
              <>
                <EuiSpacer size="xs" />
                <EuiFlexItem grow={false} style={{ width: "40px" }}>
                  <EuiIcon type="warning" color="orange" size="xl" />
                </EuiFlexItem>
              </>

              <EuiFlexItem css={{ padding: "16px" }}>
                {!noSubscription && (
                  <EuiText>
                    <b>{clientName}</b> WISdom subscription is expired on {expirationDate}.
                  </EuiText>
                )}
                {noSubscription && (
                  <EuiText>
                    <b>{clientName}</b> has no WISdom subscription.
                  </EuiText>
                )}
                <EuiText>Please select another client.</EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          }
          footer={
            <EuiFlexGroup gutterSize="m" justifyContent="flexEnd">
              <EuiFlexItem grow={false}>
                <Button type="secondary" text={"Cancel"} onClick={closeExpireModal} />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <Button
                  type="primary"
                  text={"Select Client"}
                  onClick={() => {
                    closeExpireModal();
                  }}
                />
              </EuiFlexItem>
            </EuiFlexGroup>
          }
        />
      )}
    </div>
  );
};
